import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Table, Modal, Form, Input, message, DatePicker, Popover } from 'antd';
import { filterActiveClassName } from '@components/common/FilterSelect';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import * as api from '@apis/program-schedule-group';
import { ProgramScheduleGroup, ProgramSchedule } from '@models/program-schedule';
import { useAuthentication } from '@hooks/authentication';
import { formatPhoneNumber } from '@utils/dataTransform';

const ScheduleManagementModal: React.FC<{
  open: boolean;
  onClose: () => void;
  group: ProgramScheduleGroup | null;
}> = ({ open, onClose, group }) => {
  const [scheduleIdInput, setScheduleIdInput] = useState('');
  const queryClient = useQueryClient();

  const { data: groupDetails, isLoading } = useQuery({
    queryKey: ['program-schedule-group', group?._id],
    queryFn: () => api.getProgramScheduleGroup(group!._id),
    enabled: !!group?._id,
  });

  const addSchedulesMutation = useMutation({
    mutationFn: (scheduleIds: string[]) => api.addSchedulesToGroup(group!._id, scheduleIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['program-schedule-groups'] });
      queryClient.invalidateQueries({ queryKey: ['program-schedule-group', group?._id] });
      setScheduleIdInput('');
      message.success('스케줄이 추가되었습니다.');
    },
  });

  const removeSchedulesMutation = useMutation({
    mutationFn: (scheduleIds: string[]) => api.removeSchedulesFromGroup(group!._id, scheduleIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['program-schedule-groups'] });
      queryClient.invalidateQueries({ queryKey: ['program-schedule-group', group?._id] });
      message.success('스케줄이 삭제되었습니다.');
    },
  });

  const handleAddSchedule = () => {
    if (!scheduleIdInput) return;
    addSchedulesMutation.mutate([scheduleIdInput]);
  };

  const scheduleColumns: ColumnsType<ProgramSchedule> = [
    { title: '스케줄명', dataIndex: ['schedule', 'scheduleName'] },
    { title: '대표자', dataIndex: ['representative', 'name'] },
    { title: '아이명', dataIndex: ['schedule', 'scheduleInfo', 'name'] },
    {
      title: '연락처',
      dataIndex: ['representative', 'phone'],
      render: (phone) => (phone ? formatPhoneNumber(phone) : '-'),
    },
    {
      title: '관리',
      render: (_, record) => (
        <Button danger onClick={() => removeSchedulesMutation.mutate([record._id])}>
          제거
        </Button>
      ),
    },
  ];

  return (
    <Modal open={open} onCancel={onClose} title="스케줄 관리" footer={null} width={800}>
      {isLoading ? (
        <div>Loading...</div>
      ) : (
        <div>
          <h3>그룹명: {groupDetails?.data.name}</h3>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
            <Input
              placeholder="추가할 스케줄 ID 입력"
              value={scheduleIdInput}
              onChange={(e) => setScheduleIdInput(e.target.value)}
            />
            <Button onClick={handleAddSchedule} type="primary" loading={addSchedulesMutation.isPending}>
              추가
            </Button>
          </div>
          <Table dataSource={groupDetails?.data.schedules} columns={scheduleColumns} rowKey="_id" />
        </div>
      )}
    </Modal>
  );
};

const ProgramScheduleGroupsPage: React.FC = () => {
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingGroup, setEditingGroup] = useState<ProgramScheduleGroup | null>(null);
  const [isScheduleModalVisible, setIsScheduleModalVisible] = useState(false);
  const [selectedGroupForSchedules, setSelectedGroupForSchedules] = useState<ProgramScheduleGroup | null>(null);
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  useAuthentication();

  const { data, isLoading } = useQuery({
    queryKey: ['program-schedule-groups', currentPage, pageSize, searchKeyword, startDate, endDate],
    queryFn: () =>
      api.searchProgramScheduleGroups({
        keyword: searchKeyword,
        page: currentPage - 1,
        size: pageSize,
        startDate: startDate?.toISOString(),
        endDate: endDate?.toISOString(),
      }),
  });

  const mutationConfig = {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['program-schedule-groups'] });
      setIsModalVisible(false);
      setEditingGroup(null);
      form.resetFields();
    },
    onError: (error: any) => {
      message.error(error.message || '오류가 발생했습니다.');
    },
  };

  const updateMutation = useMutation({
    mutationFn: (group: Partial<ProgramScheduleGroup>) => api.updateProgramScheduleGroup(editingGroup!._id, group),
    ...mutationConfig,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteProgramScheduleGroup(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['program-schedule-groups'] });
      message.success('그룹이 삭제되었습니다.');
    },
  });

  const handleEdit = (group: ProgramScheduleGroup) => {
    setEditingGroup(group);
    form.setFieldsValue({
      name: group.name,
      partnerMemo: group.memo?.partner,
      instructorMemo: group.memo?.instructor,
    });
    setIsModalVisible(true);
  };

  const handleDelete = (id: string) => {
    Modal.confirm({
      title: '정말로 이 그룹을 삭제하시겠습니까?',
      onOk: () => deleteMutation.mutate(id),
    });
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      const groupData: Partial<ProgramScheduleGroup> = {
        name: values.name,
        memo: {
          partner: values.partnerMemo,
          instructor: values.instructorMemo,
        },
      };

      if (editingGroup) {
        updateMutation.mutate(groupData);
      }
    } catch (info) {
      console.log('Validate Failed:', info);
    }
  };

  const columns: ColumnsType<ProgramScheduleGroup> = [
    {
      title: '시작일',
      dataIndex: 'startDate',
      render: (date) => dayjs(date).format('YYYY-MM-DD'),
      align: 'center',
    },
    {
      title: '상품 및 옵션',
      render: (_, render) => {
        return (
          <Popover
            content={
              <div>
                <div>상품명: {render.product?.name}</div>
                <div>옵션명: {render.optionName}</div>
              </div>
            }>
            <div style={{ width: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {render.optionName}
            </div>
          </Popover>
        );
      },
      align: 'center',
    },
    {
      title: '그룹명',
      dataIndex: 'name',
      align: 'center',
    },
    {
      title: '파트너명',
      dataIndex: 'schedules',
      render: (schedules) => schedules?.[0]?.partnerName || '-',
      align: 'center',
    },
    {
      title: '강사명',
      dataIndex: ['instructor', 'name'],
      align: 'center',
    },
    {
      title: '참가인원 수',
      dataIndex: 'scheduleIds',
      render: (scheduleIds) => scheduleIds?.length || 0,
      align: 'center',
    },
    {
      title: '파트너 메모',
      dataIndex: ['memo', 'partner'],
      align: 'center',
    },
    {
      title: '강사 메모',
      dataIndex: ['memo', 'instructor'],
      align: 'center',
    },
    {
      title: '관리',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
          <Button onClick={() => handleEdit(record)}>수정</Button>
          <Button danger onClick={() => handleDelete(record._id)}>
            삭제
          </Button>
          <Button onClick={() => handleOpenScheduleModal(record)}>스케줄 관리</Button>
        </div>
      ),
      align: 'center',
    },
  ];

  const handleOpenScheduleModal = (group: ProgramScheduleGroup) => {
    setSelectedGroupForSchedules(group);
    setIsScheduleModalVisible(true);
  };

  return (
    <div className="dashboard">
      <div className="page-header">
        <div>프로그램 그룹 관리</div>
        <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
          <DatePicker
            value={startDate ? dayjs(startDate) : null}
            onChange={(date) => {
              if (date) {
                setStartDate(date.toDate());
              } else {
                setStartDate(null);
              }
            }}
            placeholder="시작일"
            style={{ width: '160px' }}
          />
          <DatePicker
            value={endDate ? dayjs(endDate) : null}
            onChange={(date) => {
              if (date) {
                setEndDate(date.toDate());
              } else {
                setEndDate(null);
              }
            }}
            placeholder="종료일"
            style={{ width: '160px' }}
          />
          <Button
            onClick={() => {
              setStartDate(dayjs().startOf('day').toDate());
              setEndDate(dayjs().endOf('day').toDate());
            }}>
            오늘
          </Button>
          <Button
            onClick={() => {
              setStartDate(dayjs().startOf('week').toDate());
              setEndDate(dayjs().endOf('week').toDate());
            }}>
            이번주
          </Button>
          <Button
            onClick={() => {
              setStartDate(dayjs().startOf('month').toDate());
              setEndDate(dayjs().endOf('month').toDate());
            }}>
            이번달
          </Button>
          <Button
            onClick={() => {
              setStartDate(dayjs().add(1, 'day').startOf('day').toDate());
              setEndDate(dayjs().add(1, 'day').endOf('day').toDate());
            }}>
            내일
          </Button>
          <Button
            onClick={() => {
              setStartDate(dayjs().add(1, 'week').startOf('week').toDate());
              setEndDate(dayjs().add(1, 'week').endOf('week').toDate());
            }}>
            다음주
          </Button>
          <Button
            onClick={() => {
              setStartDate(dayjs().add(1, 'month').startOf('month').toDate());
              setEndDate(dayjs().add(1, 'month').endOf('month').toDate());
            }}>
            다음달
          </Button>
        </div>
      </div>
      <div style={{ display: 'flex', marginTop: '12px' }}>
        <div className="page-content" style={{ width: '100%', height: 'calc(100vh - 80px)', overflow: 'auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', marginBottom: '12px' }}>
            <Input
              className={filterActiveClassName(!!searchKeyword)}
              placeholder="그룹명, 강사명, 상품명, 옵션명 검색"
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              onPressEnter={() => {
                setCurrentPage(1);
                queryClient.invalidateQueries({ queryKey: ['program-schedule-groups'] });
              }}
              style={{ width: 300 }}
            />
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <Button
                onClick={() => {
                  setSearchKeyword('');
                  setCurrentPage(1);
                }}>
                검색 초기화
              </Button>
            </div>
          </div>
          <Table
            columns={columns}
            dataSource={data?.data.contents}
            loading={isLoading}
            rowKey="_id"
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: data?.data.total,
              onChange: (page, size) => {
                setCurrentPage(page);
                setPageSize(size);
              },
            }}
            scroll={{ x: 'max-content' }}
          />
        </div>
      </div>
      <Modal
        title={'그룹 수정'}
        open={isModalVisible}
        onOk={handleOk}
        onCancel={() => setIsModalVisible(false)}
        confirmLoading={updateMutation.isPending}>
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="그룹명" rules={[{ required: true, message: '그룹명을 입력해주세요.' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="partnerMemo" label="파트너 메모">
            <Input.TextArea />
          </Form.Item>
          <Form.Item name="instructorMemo" label="강사 메모">
            <Input.TextArea />
          </Form.Item>
        </Form>
      </Modal>
      <ScheduleManagementModal
        open={isScheduleModalVisible}
        onClose={() => setIsScheduleModalVisible(false)}
        group={selectedGroupForSchedules}
      />
    </div>
  );
};

export default ProgramScheduleGroupsPage;
